<template>
    <main>
        <form class="application-form" ref="applicationForm">
            <div class="card" v-if="inviteIdRequired">
                <div class="card-body">
                    <div class="form-section">
                        <h3>Your RAFAC Join Invitation</h3>
                        <div class="form-row">
                            <div class="form-group col-md-12">
                                <p class="text-muted" v-if="form.inviteId === null && !hasInviteIdFromURL">
                                    If you have an invitation ID and PIN code, you should enter them here.
                                    If you do not have one, you will need to contact
                                    your local unit and speak to them first before completing this form.
                                </p>
                                <label for="inviteId" class="required">Your invitation ID</label>
                                <input id="inviteId" name="inviteId" :class="{'is-invalid': formErrors.inviteId, 'form-control': true }" required="required"
                                       v-model="form.inviteId"
                                       placeholder="Please enter your invitation ID"
                                       v-bind:disabled="form.inviteId !== null && hasInviteIdFromURL">
                                <p class="invalid-feedback" v-if="formErrors.inviteId" v-html="formErrors.inviteId"></p>
                            </div>
                        </div>
                        <div class="form-row" v-if="inviteIdRequired">
                            <div class="form-group col-md-4 mb-0">
                                <label for="inviteSecondaryCode" class="required">Your invite PIN code</label>
                                <input id="inviteSecondaryCode" name="inviteSecondaryCode" :class="{'is-invalid': formErrors.inviteSecondaryCode, 'form-control': true }" required="required"
                                       placeholder="Invite PIN code"
                                       v-model="form.inviteSecondaryCode">
                                <p class="invalid-feedback" v-if="formErrors.inviteSecondaryCode" v-html="formErrors.inviteSecondaryCode"></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body">
                    <div class="form-section">
                        <h3>Position details</h3>

                        <!-- reCaptcha failure as a callout -->
                        <callout :message="formErrors.recaptchaToken" v-if="formErrors.recaptchaToken"></callout>

                        <div class="form-row">
                            <div class="form-group col-md-4">
                                <label for="unitType" class="required">Unit type</label>
                                <select id="unitType" :class="{'is-invalid': formErrors.unitType, 'form-control': true }" required="required" v-model="localUnitType">
                                    <option value="" disabled selected hidden>Please select a unit type...</option>
                                    <option v-for="entry in lookupUnitTypes" :value="entry.unitType">{{ entry.label }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.unitType" v-html="formErrors.unitType"></p>
                            </div>
                            <div class="form-group col-md-4">
                                <label for="unit" class="required">{{ unitLabel }}</label>
                                <select id="unit" :class="{'is-invalid': formErrors.unit, 'form-control': true }" required="required" v-model.number="form.unit" :disabled="localUnitType === ''">
                                    <option value="" disabled selected hidden>Please select a unit...</option>
                                    <option v-for="unit in lookupUnitsByType(localUnitType)" :value="unit.id">{{ unit.unitName }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.unit" v-html="formErrors.unit"></p>
                            </div>
                            <div class="form-group col-md-4">
                                <label for="role" class="required">Role</label>
                                <select id="role" :class="{'is-invalid': formErrors.role, 'form-control': true }" required="required" v-model="form.role">
                                    <option value="" disabled selected hidden>Please select a role from the list...</option>
                                    <option v-for="role in lookupRole" :value="role.id">{{ role.label }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.role" v-html="formErrors.role"></p>
                            </div>
                        </div>

                    </div>

                    <div class="form-section">
                        <h3>Personal details</h3>
                        <div class="form-row">
                            <div class="form-group col-md-2">
                                <label for="title" class="required">Title</label>
                                <select id="title" :class="{'is-invalid': formErrors.title, 'form-control': true }" required="required" v-model="form.title">
                                    <option value="">Select...</option>
                                    <option v-for="title in lookupTitle" :value="title.id">{{ title.title }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.title" v-html="formErrors.title"></p>
                            </div>
                            <div class="form-group col-md-5">
                                <label for="forename" class="required">First name</label>
                                <input type="text" :class="{'is-invalid': formErrors.forename, 'form-control': true }" id="forename"
                                       required="required"
                                       v-model="form.forename">
                                <p class="invalid-feedback" v-if="formErrors.forename" v-html="formErrors.forename"></p>
                            </div>
                            <div class="form-group col-md-5">
                                <label for="surname" class="required">Surname</label>
                                <input type="text" :class="{'is-invalid': formErrors.surname, 'form-control': true }" id="surname" required="required" v-model="form.surname">
                                <p class="invalid-feedback" v-if="formErrors.surname" v-html="formErrors.surname"></p>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group col-md-6">
                                <label for="knownAs">Known As</label>
                                <input type="text" :class="{'is-invalid': formErrors.knownAs, 'form-control': true }" id="knownAs" v-model="form.knownAs">
                                <p class="invalid-feedback" v-if="formErrors.knownAs" v-html="formErrors.knownAs"></p>
                                <p class="mt-1 text-muted small">
                                    "Known as" allows you to choose a different first name to that of your legal name. It is
                                    used in your display name and will form the first part of your username.
                                </p>
                            </div>
                            <div class="form-group col-md-6">
                                <label for="dateofbirth" class="required">Date of Birth</label>
                                <input type="date" id="dateofbirth" :class="{'is-invalid': formErrors.dateOfBirth, 'form-control': true }"
                                       required="required"
                                       v-model="form.dateOfBirth">
                                <p class="invalid-feedback" v-if="formErrors.dateOfBirth" v-html="formErrors.dateOfBirth"></p>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group col-12">
                                <label>Email Preview</label>
                                <p v-if="emailPreview">{{emailPreview}}</p>
                                <p v-else>Please complete the form to preview your email address.</p>
                                <p class="mt-1 text-muted small">The number "100" is an example. Your final email address will be assigned a unique number
                                    based on system availability.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="form-section">
                        <h3>Personal details</h3>
                        <div class="form-row">
                            <div class="form-group col-md-6">
                                <label for="gender" class="required">Gender</label>
                                <select id="gender" :class="{'is-invalid': formErrors.gender, 'form-control': true }" required="required" v-model="form.gender">
                                    <option value="" disabled selected hidden>Please choose from the list...</option>
                                    <option v-for="gender in lookupGender" :value="gender.id">{{ gender.gender }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.gender" v-html="formErrors.gender"></p>
                            </div>
                            <div class="form-group col-md-6">
                                <label for="pronoun" class="required">Pronouns</label>
                                <select id="pronoun" :class="{'is-invalid': formErrors.pronoun, 'form-control': true }" required="required" v-model="form.pronoun">
                                    <option value="" disabled selected hidden>Please choose from the list...</option>
                                    <option v-for="pronoun in lookupPronoun" :value="pronoun.id">{{ pronoun.pronoun }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.pronoun" v-html="formErrors.pronoun"></p>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group col-md-6">
                                <label for="ethnicity" class="required">Ethnicity</label>
                                <select id="ethnicity" :class="{'is-invalid': formErrors.ethnicity, 'form-control': true }" required="required" v-model="form.ethnicity">
                                    <option value="" disabled selected hidden>Please choose from the list...</option>
                                    <optgroup v-for="obj in lookupEthnicity" :label="obj.group">
                                        <option v-for="ethnicity in obj.entries" :value="ethnicity.id">{{ ethnicity.ethnicity }}</option>
                                    </optgroup>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.ethnicity" v-html="formErrors.ethnicity"></p>
                            </div>
                            <div class="form-group col-md-6">
                                <label for="religion" class="required">Religion</label>
                                <select id="religion" :class="{'is-invalid': formErrors.religion, 'form-control': true }" required="required" v-model="form.religion">
                                    <option value="" disabled selected hidden>Please choose from the list...</option>
                                    <template v-for="obj in lookupReligion">
                                        <option v-if="!obj.group" v-for="religion in obj.entries" :value="religion.id">{{ religion.religion }}</option>
                                        <optgroup v-if="obj.group" :label="obj.group">
                                            <option v-for="religion in obj.entries" :value="religion.id">{{ religion.religion }}</option>
                                        </optgroup>
                                    </template>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.religion" v-html="formErrors.religion"></p>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group col-md-6">
                                <label for="nationality" class="required">Nationality</label>
                                <input type="text" :class="{'is-invalid': formErrors.nationality, 'form-control': true }" id="nationality" name="nationality"
                                       required="required"
                                       v-model="form.nationality">
                                <p class="invalid-feedback" v-if="formErrors.nationality" v-html="formErrors.nationality"></p>
                            </div>
                            <div class="form-group col-md-6">
                                <label for="countryOfBirth" class="required">Country of birth</label>
                                <select id="countryOfBirth" :class="{'is-invalid': formErrors.countryOfBirth, 'form-control': true }" required="required" v-model="form.countryOfBirth">
                                    <option value="">Please choose from the list...</option>
                                    <option v-for="country in lookupCountry" :value="country.countryCode">{{ country.country }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.countryOfBirth" v-html="formErrors.countryOfBirth"></p>
                            </div>
                        </div>

                    </div>

                    <div class="form-section">
                        <h3>Personal Details</h3>
                        <div class="form-row">
                            <div class="form-group col-md-6">
                                <label for="employer">Employer</label>
                                <input type="text" class="form-control" id="employer" v-model="form.employer">
                            </div>
                            <div class="form-group col-md-6">
                                <label for="profession" class="required">Profession</label>
                                <select id="profession" :class="{'is-invalid': formErrors.profession, 'form-control': true }" required="required" v-model="form.profession">
                                    <option value="" disabled selected hidden>Please choose from the list...</option>
                                    <option v-for="profession in lookupProfession" :value="profession.id">{{ profession.profession }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.profession" v-html="formErrors.profession"></p>
                            </div>
                        </div>
                    </div>

                    <div class="form-section">
                        <h3>Contact details</h3>
                        <div class="form-row">
                            <p>
                                Please provide your contact details using the form below. Following your initial
                                application you will receive a series of welcome emails that provide access to
                                additional Air Cadet systems which will allow you to complete your application process.
                            </p>
                            <div class="form-group col-md-4">
                                <label for="property" class="required">Property Name or Number</label>
                                <input type="text" :class="{'is-invalid': formErrors.property, 'form-control': true }" id="property" name="property"
                                       required="required"
                                       v-model="form.property">
                                <p class="invalid-feedback" v-if="formErrors.property" v-html="formErrors.property"></p>
                            </div>
                            <div class="form-group col-md-4">
                                <label for="street" class="required">Street Name</label>
                                <input type="text" :class="{'is-invalid': formErrors.street, 'form-control': true }" id="street" required="required" v-model="form.street">
                                <p class="invalid-feedback" v-if="formErrors.street" v-html="formErrors.street"></p>
                            </div>
                            <div class="form-group col-md-4">
                                <label for="town" class="required">Town</label>
                                <input type="text" :class="{'is-invalid': formErrors.town, 'form-control': true }" id="town" required="required" v-model="form.town">
                                <p class="invalid-feedback" v-if="formErrors.town" v-html="formErrors.town"></p>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group col-md-4">
                                <label for="county" class="required">County</label>
                                <input type="text" :class="{'is-invalid': formErrors.county, 'form-control': true }" id="county" required="required" v-model="form.county">
                                <p class="invalid-feedback" v-if="formErrors.county" v-html="formErrors.county"></p>
                            </div>
                            <div class="form-group col-md-4">
                                <label for="postcode" class="required">Postcode</label>
                                <input type="text" :class="{'is-invalid': formErrors.postcode, 'form-control': true }" id="postcode" name="postcode"
                                       required="required"
                                       v-model="form.postcode">
                                <p class="invalid-feedback" v-if="formErrors.postcode" v-html="formErrors.postcode"></p>
                            </div>
                            <div class="form-group col-md-4">
                                <label for="countrycode" class="required">Country</label>
                                <select id="countrycode" :class="{'is-invalid': formErrors.countryCode, 'form-control': true }" required="required" v-model="form.countryCode">
                                    <option value="">Please choose from the list...</option>
                                    <option v-for="country in lookupCountry" :value="country.countryCode">{{ country.country }}</option>
                                </select>
                                <p class="invalid-feedback" v-if="formErrors.countryCode" v-html="formErrors.countryCode"></p>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group col-md-4">
                                <label for="homephone">Home Phone</label>
                                <input type="text" class="form-control" id="homephone" v-model="form.homePhone">
                            </div>
                            <div class="form-group col-md-4">
                                <label for="mobilephone" class="required">Mobile Phone</label>
                                <input type="text" :class="{'is-invalid': formErrors.mobilePhone, 'form-control': true }" id="mobilephone" name="mobilephone"
                                       required="required"
                                       v-model="form.mobilePhone">
                                <p class="invalid-feedback" v-if="formErrors.mobilePhone" v-html="formErrors.mobilePhone"></p>
                            </div>
                            <div class="form-group col-md-4">
                                <label for="email" class="required">Email</label>
                                <input type="email" :class="{'is-invalid': formErrors.email, 'form-control': true }" id="email" required="required" v-model="form.email">
                                <p class="invalid-feedback" v-if="formErrors.email" v-html="formErrors.email"></p>
                            </div>
                        </div>
                    </div>

                    <div class="form-section">
                        <div class="form-row">
                            <h3>Agreement</h3>
                            <p>
                                I understand that the Cadet Forces Adult Volunteer position I have applied for is a position whose
                                normal duties include caring for, training, supervising or being in sole charge of children.
                            </p>
                            <p>
                                I understand that in England, Wales and Northern Ireland this is regulated activity and that
                                in Scotland this is a regulated role.
                            </p>
                            <p>
                                I understand that Statutory provisions relevant to the jurisdiction of my application apply
                                and in particular the Rehabilitation of Offenders Act 1974 (Exceptions) Order 1975, the Exclusions
                                and Exceptions (Scotland) Order 2003, the Police Act 1997, the Safeguarding of Vulnerable
                                Groups Act 2006 as modified by the Protection of Freedoms Act 2012, and the Protection of
                                Vulnerable Groups (Scotland) Act 2007).
                            </p>
                            <p>
                                By signing the below, I understand, agree, and accept that if I am appointed as a volunteer
                                in the RAF Air Cadets, I must meet and adhere to the following declarations:
                            </p>
                            <ol type="a">
                                <li>I have not been disqualified from working with children or subject to a Disqualification
                                    Order within the meaning of the Criminal Justice and Court Services Act 2000.</li>
                                <li>I understand that this appointment is conditional upon the receipt of a enhanced
                                    criminal records check from Disclosure and Barring Services, AccessNI or Disclosure
                                    Scotland that has been checked and assessed as confirming my suitability to appointment
                                    by RAFAC Safeguarding on behalf of Comdt RAFAC. </li>
                                <li>I understand that I may be required to give my consent for RAFAC SG to make appropriate
                                    enquiries to any other relevant authority in order to confirm my suitability for appointment.</li>
                                <li>I give my consent, under the provisions of the Data Protection Act 1998/GDPR 2018, for personal
                                    sensitive data held by the RAF, Army, Navy or MOD to be disclosed to HQ RAF Air Cadets
                                    for the benefit of this application. I understand that personal information held by the
                                    RAFAC may also be shared with the relevant MOD or RAF authorities, including other MOD
                                    sponsored Cadet Forces.</li>
                                <li>I will inform the RAFAC Safeguarding Manager if I am charged with or summonsed to appear in court
                                    for a criminal offence. </li>
                                <li>I will inform the RAFAC if I am the subject of a police, social services, MOD/RAF or professional
                                    body child protection related investigation after I am appointed.</li>
                                <li>I understand that failure to do so may lead to the immediate suspension of my appointment in the RAFAC
                                    and/or termination of RAFAC service in accordance with current RAFAC regulations.</li>
                                <li>I agree to abide by Air Cadet Publication (ACP) 1 (Ethos, Core Values and Standards of the RAF Air Cadets
                                    and ACP 4 (Safeguarding and Protecting Children) and I understand that personal relationships
                                    between adult members of staff and cadets of any age are strictly forbidden.</li>
                                <li>I hereby give my explicit consent for the RAF Air Cadets to apply for an Enhanced Disclosure from the
                                    Disclosure and Barring Service (DBS), Protecting Vulnerable Groups (PVG) scheme, or AccessNI, as
                                    appropriate to my location. I understand that this check will include a search of the Children’s
                                    Barred List and, where directed by RAFAC HQ Safeguarding & People Services, may also include a check
                                    of the Vulnerable Adults Barred List.</li>
                                <li>I agree to provide the necessary identification documentation via the approved digital platform or in person
                                    as required to facilitate this check. Furthermore, I understand that the RAF Air Cadets may require me to subscribe
                                    to the relevant Update Service to allow for periodic status checks during my tenure, and I hereby grant consent for
                                    such checks to be performed. I also consent to criminal record check renewals or additional checks being undertaken
                                    if directed by RAFAC HQ (for example, in relation to Safeguarding or Disciplinary casework concerns).</li>
                            </ol>
                            <p>Declaration of Truth</p>
                            <ol>
                                <li style="list-style-type: none;">I certify that the information contained in this form is true and correct to the best of my knowledge and I
                                    realise that false information or wilful omissions may lead to the immediate suspension or termination of my
                                    appointment with the RAFAC.</li>
                            </ol>
                            <div class="form-group">
                                <div class="form-check">
                                    <input :class="{'is-invalid': formErrors.agreementAccepted, 'form-check-input': true }" type="checkbox" id="gridCheck" name="agreement"
                                           required="required" v-model="form.agreementAccepted">
                                    <label class="form-check-label required" for="gridCheck">
                                        I confirm that I have understood and agree to all statements above.</label>
                                    <p class="invalid-feedback" v-if="formErrors.agreementAccepted" v-html="formErrors.agreementAccepted"></p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="form-navigation">
                        <button class="btn bg-bader text-white pull-right" v-on:click="submitApplication" v-bind:disabled="submittingApplication">{{ submitButtonText }}</button>
                        <span class="clearfix"></span>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<style scoped>
label.required:after {
    content: " *";
    color: #ff0000;
}
</style>

<script>

import axios from 'axios';
import Swal from 'sweetalert2';
import sortBy from 'lodash/sortBy';
import callout from "../../shared/components/callout";
import { useReCaptcha } from 'vue-recaptcha-v3';

export default {
    setup() {
        const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();
        const recaptchaSubmit = function (actionType)  {
            return recaptchaLoaded()
                .then(() => {
                    return executeRecaptcha(actionType);
                });
        }

        return {
            recaptchaSubmit
        };
    },
    components: {
        callout
    },
    props: {
        inviteIdRequired: Boolean,
        submitUrl: String,
        completionUrl: String,
        unitsUrl: String,
        genderUrl: String,
        pronounUrl: String,
        ethnicityUrl: String,
        religionUrl: String,
        countryUrl: String,
        professionUrl: String,
        titleUrl: String
    },
    data() {
        return {
            recaptchaActionType: 'application/submit/cfav',

            // Form data
            form: {
                recaptchaToken: '',
                inviteId: null,
                inviteSecondaryCode: null,
                unit: '',
                role: '',
                forename: '',
                surname: '',
                knownAs: '',
                title: '',
                dateOfBirth: null,
                gender: '',
                pronoun: '',
                ethnicity: '',
                religion: '',
                profession: '',
                nationality: '',
                countryOfBirth: '',
                employer: '',
                property: '',
                street: '',
                town: '',
                county: '',
                postcode: '',
                countryCode: '',
                homePhone: '',
                mobilePhone: '',
                email: '',
                agreementAccepted: false
            },

            formErrors: {},

            // Data lookups
            sourceUnits: [],
            sourceGender: [],
            sourcePronoun: [],
            sourceEthnicity: [],
            sourceReligion: [],
            sourceCountry: [],
            sourceProfession: [],
            sourceTitle: [],

            // Controls
            submittingApplication: false,
            hasInviteIdFromURL: false,

            // Form mapping
            mapStringToNullFields: [
                'unit', 'role', 'gender', 'ethnicity', 'religion', 'pronoun', 'profession'
            ],

            // Misc
            timerMs: 1500,
            localUnitType: ''
        }
    },
	watch: {
		localUnitType(newVal) {
			// If the unit type changes and it's no longer CCF, 
			// check if the currently selected role is the CCF-only one
			if (newVal.toLowerCase() !== 'ccf' && this.form.role === 2285) {
				this.form.role = ''; // Reset the selection
			}
		}
	},
    computed: {
		lookupRole() {
			// Define your base roles that everyone sees
			let roles = [
				{ id: 275, label: "Civilian Instructor" },
				{ id: 276, label: "Civilian Gliding Instructor" },
				{ id: 277, label: "Chaplain" },
				{ id: 1281, label: "Civilian Committee Member" },
			];

			// Add the CCF specific role only if unit type is 'ccf'
			// if (this.localUnitType.toLowerCase() === 'ccf') {
			// 	roles.push({ id: 2285, label: "Senior Non-Commissioned Officer" }); 
			// }

			return roles;
		},
        submitButtonText: function () {
            return this.submittingApplication ? 'Please wait, submitting...' : 'Submit your application';
        },

        // Lookup mapping - interject here if you need to do eg sorting
        lookupUnitTypes() {
            let unitTypes = [];
            let self = this;
            this.sourceUnits.map(function (unit) {
                if (unitTypes.indexOf(unit.unitType) === -1) {
                    unitTypes.push(unit.unitType);
                }

                return unit;
            });

            // return a nice object to iterate over
            let unitTypesObjs = unitTypes.map(function (unitType) {
                return {
                    unitType: unitType,
                    label: self.unitLabelByType(unitType)
                }
            });
            return sortBy(unitTypesObjs, ['label']);
        },
        lookupUnits() { return sortBy(this.sourceUnits, ['unitName']); },
        lookupGender() { return this.sourceGender; },
        lookupPronoun() { return this.sourcePronoun; },
        lookupEthnicity() { return this.sourceEthnicity; },
        lookupReligion() { return this.sourceReligion; },
        lookupCountry() { return this.sourceCountry; },
        lookupProfession() { return this.sourceProfession; },
        lookupTitle() { return this.sourceTitle; },

        // Misc
        unitLabel() {
            if (this.localUnitType === '') {
                return 'Unit';
            }

            return 'Unit (' + this.unitLabelByType(this.localUnitType) + ')';
        },

        // Email Address
        emailPreview() {
            // 1. Logic: Use Alias if present, otherwise First Name
            const firstPart = this.form.knownAs ? this.form.knownAs : this.form.forename;
            const lastPart = this.form.surname;

            // 2. Don't show a partial email if fields are empty
            if (!firstPart || !lastPart) {
                return null;
            }

            // 3. Helper function (defined here or outside)
            const sanitize = (text) => {
                return text
                    .toLowerCase()
                    .trim()
                    .replace(/\s+/g, '-')         // Replace spaces with hyphens
                    .replace(/[^a-z0-9-]/g, '');  // Remove invalid chars (but keep hyphens!)
            };

            const cleanFirst = sanitize(firstPart);
            const cleanLast = sanitize(lastPart);

            return `${cleanFirst}.${cleanLast}100@rafac.mod.gov.uk`;
        }
    },
    methods: {
        lookupUnitsByType(unitType) {
            return this.lookupUnits.filter(unit => unit.unitType.toLowerCase() === unitType.toLowerCase());
        },
        unitLabelByType(unitType) {
            switch (unitType.toLowerCase()) {
                case 'sqn':
                    return 'Squadron';
                case 'ccf':
                    return 'CCF School';
                case 'twofts':
                    return '2 FTS';
                case 'vgs':
                    return 'VGS';
                case 'whq':
                    return 'Wing';
                default:
                    return '';
            }
        },
        async submitApplication () {
            if (!this.submitUrl) {
                throw new Error('Submit URL not defined');
            }

            this.submittingApplication = true;
            this.form.recaptchaToken = await this.recaptchaSubmit(this.recaptchaActionType)

            let data = Object.assign({}, this.form);
            this.mapStringToNullFields.forEach((field) => {
                data[field] = data[field] === '' ? null : data[field];
            });

            let self = this;
            Swal.fire({
                title: 'Please wait...',
                text: "We are currently validating your application, this may take a few moments...",
                showConfirmButton: false
            });
            axios.post(this.submitUrl, data)
                .then(response => {
                    // Submission successful
                    Swal.fire({
                        icon: 'success',
                        title: '',
                        text: 'Your application has been submitted',
                        showConfirmButton: false,
                        timer: this.timerMs
                    }).then(function() {
                        window.location = self.completionUrl;
                    });
                })
                .catch(error => {
                    if (error.response) {
                        let collatedErrors = self.collateErrors({}, error.response.data)
                        self.formErrors = self.flattenArrays(collatedErrors, '<br>')

                        // Cheap hack to flag unitType as an error as well
                        if (self.localUnitType === '' || self.lookupUnitTypes.map((entry) => entry.key).includes(self.localUnitType)) {
                            self.formErrors.unitType = 'Please select a unit type';
                        }
                    }
                    Swal.fire({
                        icon: 'error',
                        title: '',
                        text: 'There was a problem submitting your application',
                        showConfirmButton: false,
                        timer: this.timerMs
                    }).then(() => {
                        window.scroll({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                        });
                    });
                    this.submittingApplication = false;
                    this.form.recaptchaToken = '';
                });
        },

        // Pulled from Node-UI
        flattenArrays: (input, joinChar) => {
            for (let key in input) {
                if (!input.hasOwnProperty(key)) {
                    continue;
                }

                // Only operate on arrays or objects
                if (Array.isArray(input[key])) {

                    for (let i in input[key]) {
                        if (!input[key].hasOwnProperty(i)) {
                            continue;
                        }

                        // Handle objects in arrays
                        if (Object.prototype.toString.call(input[key][i]) === '[object Object]') {
                            input[key][i] = this.flattenArrays(input[key][i], joinChar);
                            continue;
                        }

                        if (Array.isArray(input[key])) {
                            input[key] = input[key].join(joinChar);
                        }
                    }
                }
                if (Object.prototype.toString.call(input[key]) === '[object Object]') {
                    input[key] = this.flattenArrays(input[key], joinChar);
                }
            }
            return input;
        },
        collateErrors: (collatedObj, errors) => {
            for (let key in errors) {
                if (!errors.hasOwnProperty(key))
                    continue;

                if (key.includes('.')) {
                    // Nested - must be an object or array at topKey
                    let parts = key.split('.');
                    let topKey = parts[0];
                    let subKey = parts[1];

                    collatedObj[topKey] = collatedObj[topKey] || {};

                    // Handle arrays (eg foo.1.bar)
                    if (parts.length === 3) {
                        let arrIdx = parts[1];
                        subKey = parts[2];

                        collatedObj[topKey][arrIdx] = collatedObj[topKey][arrIdx] || {};
                        collatedObj[topKey][arrIdx][subKey] = collatedObj[topKey][arrIdx][subKey] || [];
                        collatedObj[topKey][arrIdx][subKey].push(...errors[key]);
                    } else {
                        collatedObj[topKey][subKey] = collatedObj[topKey][subKey] || [];
                        collatedObj[topKey][subKey].push(...errors[key]);
                    }
                } else {
                    collatedObj[key] = collatedObj[key] || [];
                    collatedObj[key].push(...errors[key]);
                }
            }

            return collatedObj;
        }
    },
    mounted() {
        // Get invitation ID from URL, if it's there and if we have it enabled
        if (this.inviteIdRequired) {
            const queryString = window.location.search.toLowerCase(); // normalise to lower case
            const urlParams = new URLSearchParams(queryString);
            const urlInviteId = (urlParams.get('inviteid') || '').trim();
            this.form.inviteId = urlInviteId || null;
            this.hasInviteIdFromURL = this.form.inviteId?.length > 0;
        }

        // Load data sources
        // Note that we return null from the catch to avoid one request failing everything else
        const unitsRequest = axios.get(this.unitsUrl).catch(err => null);
        const genderRequest = axios.get(this.genderUrl).catch(err => null);
        const pronounRequest = axios.get(this.pronounUrl).catch(err => null);
        const ethnicityRequest = axios.get(this.ethnicityUrl).catch(err => null);
        const religionRequest = axios.get(this.religionUrl).catch(err => null);
        const countryRequest = axios.get(this.countryUrl).catch(err => null);
        const professionRequest = axios.get(this.professionUrl).catch(err => null);
        const titleRequest = axios.get(this.titleUrl).catch(err => null);
        Promise.all([unitsRequest, genderRequest, pronounRequest, ethnicityRequest, religionRequest, countryRequest, professionRequest, titleRequest])
            .then((responses) => {
                this.sourceUnits = responses[0] && responses[0].data;
                this.sourceGender = responses[1] && responses[1].data;
                this.sourcePronoun = responses[2] && responses[2].data;
                this.sourceEthnicity = responses[3] && responses[3].data;
                this.sourceReligion = responses[4] && responses[4].data;
                this.sourceCountry = responses[5] && responses[5].data;
                this.sourceProfession = responses[6] && responses[6].data;
                this.sourceTitle = responses[7] && responses[7].data;
            });
    }
};
</script>
